<template>
  <div class="category-selector-wrapper" ref="wrapper">
    <div
      class="category-selector"
      :class="{ 'is-fixed': isFixed }"
      ref="selector"
    >
      <div class="scroll-container" ref="scrollContainer">
        <div
          v-for="item in categories"
          :key="item.url"
          class="category-item"
          :class="{ 'is-active': currentCategory === item.url }"
          @click="handleCategoryClick(item)"
          @touchstart.passive="handleTouchStart"
        >
          <div class="title">{{ item.title }}</div>
          <div class="subtitle">{{ item.subtitle }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from "vue";
import { getNavigationList, transformCategoryData } from "@/api/advertisement";

export default {
  name: "CategorySelector",

  props: {
    // ... 原有的props
  },

  emits: ["category-change", "update-images"],

  setup(props, { emit }) {
    // 状态定义
    const categories = ref([]);
    const currentCategory = ref("");
    const isFixed = ref(false);
    const isProcessingFixed = ref(false);
    const isProcessingUnfixed = ref(false);

    // DOM引用
    const wrapper = ref(null);
    const selector = ref(null);
    const scrollContainer = ref(null);

    // 数据加载
    const loadCategories = async () => {
      try {
        // console.log("[CategorySelector] 开始加载分类数据");
        const response = await getNavigationList();

        if (response?.Data) {
          // 准备数据
          const processedCategories = transformCategoryData(response.Data);
          const categoryImages = {};

          // 打印原始数据
          // console.log("[CategorySelector] 原始数据:", response.Data);

          // 遍历处理每个分类的图片
          response.Data.forEach((item, index) => {
            try {
              const advertCode = item.Url; // 使用Url作为advertCode
              if (!advertCode) {
                console.warn(`[CategorySelector] 项目 ${index} 缺少URL:`, item);
                return;
              }

              // 处理图片URL
              if (item.ImageUrl) {
                try {
                  const imageData = JSON.parse(item.ImageUrl);
                  if (Array.isArray(imageData) && imageData.length > 0) {
                    const { ServerUrl, FilePath } = imageData[0];
                    if (ServerUrl && FilePath) {
                      // 使用advertCode作为key存储图片信息
                      categoryImages[advertCode] = {
                        imageUrl: `${ServerUrl}${FilePath}`,
                        url: advertCode,
                        title: item.NavigationName,
                      };

                      // console.log(`[CategorySelector] 成功映射分类图片:`, {
                      //   advertCode,
                      //   url: advertCode,
                      //   imageUrl: `${ServerUrl}${FilePath}`,
                      //   title: item.NavigationName,
                      // });
                    }
                  }
                } catch (e) {
                  console.error(`[CategorySelector] 解析图片数据失败:`, e);
                }
              }
            } catch (e) {
              console.error(`[CategorySelector] 处理分类数据失败:`, e);
            }
          });

          // 验证处理结果
          // console.log("[CategorySelector] 数据处理完成:", {
          //   categoriesCount: processedCategories.length,
          //   imagesCount: Object.keys(categoryImages).length,
          //   categories: processedCategories,
          //   imageMapping: categoryImages,
          // });

          // 确保分类数量和图片映射数量一致
          if (
            Object.keys(categoryImages).length !== processedCategories.length
          ) {
            console.warn(
              "[CategorySelector] 警告: 分类数量与图片映射数量不匹配",
              {
                categoriesCount: processedCategories.length,
                imagesCount: Object.keys(categoryImages).length,
              }
            );
          }

          // 发送完整的图片映射数据
          emit("update-images", categoryImages);

          // 更新分类数据
          categories.value = processedCategories;

          if (processedCategories.length > 0) {
            currentCategory.value = processedCategories[0].url;
          }
        }
      } catch (error) {
        console.error("[CategorySelector] 加载分类数据失败:", error);
      }
    };

    // 分类点击处理
    const handleCategoryClick = (category) => {
      currentCategory.value = category.url;
      emit("category-change", category);
    };

    // 固定状态理
    const handleScroll = () => {
      if (!wrapper.value || !selector.value) return;

      const wrapperRect = wrapper.value.getBoundingClientRect();
      const headerHeight = 44; // 假设header高度为50px，实际应该从配置或DOM中获取

      if (
        !isProcessingFixed.value &&
        !isFixed.value &&
        wrapperRect.top <= headerHeight + 5
      ) {
        isProcessingFixed.value = true;
        isFixed.value = true;
        setTimeout(() => {
          isProcessingFixed.value = false;
        }, 100);
      } else if (
        !isProcessingUnfixed.value &&
        isFixed.value &&
        wrapperRect.top > headerHeight + 5
      ) {
        isProcessingUnfixed.value = true;
        isFixed.value = false;
        setTimeout(() => {
          isProcessingUnfixed.value = false;
        }, 100);
      }
    };

    // 生命周期钩子
    onMounted(() => {
      loadCategories();
      window.addEventListener("scroll", handleScroll, { passive: true });
    });

    onUnmounted(() => {
      window.removeEventListener("scroll", handleScroll);
    });

    return {
      categories,
      currentCategory,
      isFixed,
      wrapper,
      selector,
      scrollContainer,
      handleCategoryClick,
    };
  },
};
</script>

<style scoped>
.category-selector-wrapper {
  width: 100%;
  height: 80px;
}

.category-selector {
  width: 100%;
  height: 80px;
  background: #ffffff;
  transition: all 0.3s;
}

.category-selector.is-fixed {
  position: fixed;
  top: 44px;
  left: 0;
  z-index: 100;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.scroll-container {
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  height: 100%;
  padding: 0 16px;
}

.category-item {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 100%;
  margin-right: 16px;
  cursor: pointer;
}

.category-item .title {
  font-size: 16px;
  color: #333;
  margin-bottom: 4px;
}

.category-item .subtitle {
  font-size: 12px;
  color: #999;
  padding: 2px 6px;
  border-radius: 10px;
}

.category-item.is-active .title {
  color: rgb(255, 0, 0);
}

.category-item.is-active .subtitle {
  color: #fff;
  background-color: #f00;
}
</style>
